import {
  mappedParkedRoadVehicle,
  VILNIUS_ROAD_VEHICLES,
  type RoadVehicleSpec,
} from "../../config/vehicles.ts";
import {
  addDynamicRoadVehicleSpecs,
  createInitialRoadVehicleFleetState,
  ROAD_VEHICLE_FLEET_SCHEMA_VERSION,
  type RoadVehicleFleetState,
} from "./index.ts";
import type { RuntimeParkedVehicleFamily } from
  "../../world/schema/runtimeTile.ts";

export const ROAD_VEHICLE_SAVE_KEY = "vilnius.road-vehicles.v1";

export function serializeRoadVehicleFleet(state: Readonly<RoadVehicleFleetState>): string {
  return JSON.stringify(state);
}

export function parseRoadVehicleFleet(value: string): RoadVehicleFleetState {
  const parsed: unknown = JSON.parse(value);
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) throw new Error("Road vehicle save must be an object");
  const record = parsed as Record<string, unknown>;
  const schemaVersion = Number(record.schemaVersion);
  if (![1, 2, 3, ROAD_VEHICLE_FLEET_SCHEMA_VERSION].includes(schemaVersion)) throw new Error("Unsupported road vehicle save schemaVersion");
  if (!Number.isSafeInteger(record.tick) || Number(record.tick) < 0) throw new Error("Road vehicle save tick is invalid");
  if (typeof record.vehiclesById !== "object" || record.vehiclesById === null || Array.isArray(record.vehiclesById)) throw new Error("Road vehicle save fleet is invalid");
  const vehicles = record.vehiclesById as Record<string, unknown>;
  const dynamicSpecs = schemaVersion >= 3
    ? parseDynamicSpecs(record.dynamicSpecsById, schemaVersion)
    : [];
  const allSpecs = [...VILNIUS_ROAD_VEHICLES, ...dynamicSpecs];
  const expectedVehicleIds = new Set(allSpecs.map((spec) => spec.id));
  if (Object.keys(vehicles).some((vehicleId) => !expectedVehicleIds.has(vehicleId))) {
    throw new Error("Road vehicle save contains an unexpected fleet");
  }
  for (const [vehicleId, value] of Object.entries(vehicles)) {
    const spec = allSpecs.find((entry) => entry.id === vehicleId);
    if (spec === undefined || value === null || typeof value !== "object" || Array.isArray(value)) throw new Error(`Road vehicle save ${vehicleId} is invalid`);
    const vehicle = value as Record<string, unknown>;
    if (vehicle.id !== spec.id) throw new Error(`Road vehicle ${spec.id} ID is invalid`);
    for (const field of ["yawRadians", "pitchRadians", "rollRadians", "speedMps", "steeringRadians"] as const) if (!Number.isFinite(vehicle[field])) throw new Error(`Road vehicle ${spec.id} ${field} is invalid`);
    const position = vehicle.position as Record<string, unknown> | undefined;
    if (position === undefined || position === null || typeof position !== "object" || Array.isArray(position) || ![position.x, position.y, position.z].every(Number.isFinite)) throw new Error(`Road vehicle ${spec.id} position is invalid`);
    const occupants = vehicle.occupantBySeatId;
    if (typeof occupants !== "object" || occupants === null || Array.isArray(occupants)) throw new Error(`Road vehicle ${spec.id} occupants are invalid`);
    const seatIds = new Set(spec.seats.map((seat) => seat.id));
    for (const [seatId, occupantId] of Object.entries(occupants)) {
      if (!seatIds.has(seatId) || typeof occupantId !== "string" || occupantId.length === 0) throw new Error(`Road vehicle ${spec.id} seat occupancy is invalid`);
    }
  }
  const initialFleet = addDynamicRoadVehicleSpecs(
    createInitialRoadVehicleFleetState(),
    dynamicSpecs,
  );
  const normalizedVehicles = Object.fromEntries(
    allSpecs.map((spec) => {
      const initial = initialFleet.vehiclesById[spec.id]!;
      const raw = vehicles[spec.id];
      if (raw === undefined) return [spec.id, initial];
      const vehicle = raw as Record<string, unknown>;
      const position = vehicle.position as Record<string, number>;
      const lastSafePosition = validPosition(vehicle.lastSafePosition)
        ? vehicle.lastSafePosition
        : position;
      return [spec.id, {
        ...initial,
        id: spec.id,
        position: { x: position.x, y: position.y, z: position.z },
        yawRadians: Number(vehicle.yawRadians),
        pitchRadians: Number(vehicle.pitchRadians),
        rollRadians: Number(vehicle.rollRadians),
        speedMps: Number(vehicle.speedMps),
        steeringRadians: Number(vehicle.steeringRadians),
        longitudinalAccelerationMps2: finiteOr(
          vehicle.longitudinalAccelerationMps2,
          0,
        ),
        wheelRotationRadians: finiteOr(vehicle.wheelRotationRadians, 0),
        visualPitchRadians: finiteOr(vehicle.visualPitchRadians, 0),
        visualRollRadians: finiteOr(vehicle.visualRollRadians, 0),
        suspensionCompressionM: finiteOr(
          vehicle.suspensionCompressionM,
          0,
        ),
        surfaceKind:
          vehicle.surfaceKind === "road" ||
          vehicle.surfaceKind === "hardscape" ||
          vehicle.surfaceKind === "terrain"
            ? vehicle.surfaceKind
            : "road",
        surfaceId:
          typeof vehicle.surfaceId === "string" || vehicle.surfaceId === null
            ? vehicle.surfaceId
            : null,
        headlightsOn: booleanOr(vehicle.headlightsOn, false),
        brakeLightsOn: booleanOr(vehicle.brakeLightsOn, false),
        reverseLightsOn: booleanOr(vehicle.reverseLightsOn, false),
        leftIndicatorOn: booleanOr(vehicle.leftIndicatorOn, false),
        rightIndicatorOn: booleanOr(vehicle.rightIndicatorOn, false),
        beaconOn: booleanOr(vehicle.beaconOn, false),
        sirenOn: booleanOr(vehicle.sirenOn, false),
        hornActive: false,
        lookBehindActive: false,
        handbrakeActive: false,
        activeDoorSide:
          vehicle.activeDoorSide === "left" || vehicle.activeDoorSide === "right"
            ? vehicle.activeDoorSide
            : null,
        doorOpenSecondsRemaining: Math.max(
          0,
          finiteOr(vehicle.doorOpenSecondsRemaining, 0),
        ),
        impactIntensity01: clamp01(finiteOr(vehicle.impactIntensity01, 0)),
        damage01: clamp01(finiteOr(vehicle.damage01, 0)),
        lastSafePosition: { ...lastSafePosition },
        lastSafeYawRadians: finiteOr(
          vehicle.lastSafeYawRadians,
          Number(vehicle.yawRadians),
        ),
        occupantBySeatId: vehicle.occupantBySeatId as Record<string, string>,
      }];
    }),
  ) as RoadVehicleFleetState["vehiclesById"];
  if (typeof record.mountByPlayerId !== "object" || record.mountByPlayerId === null || Array.isArray(record.mountByPlayerId)) throw new Error("Road vehicle save mounts are invalid");
  const normalizedMounts: Record<string, RoadVehicleFleetState["mountByPlayerId"][string]> = {};
  for (const [playerId, value] of Object.entries(record.mountByPlayerId)) {
    if (playerId.length === 0 || typeof value !== "object" || value === null || Array.isArray(value)) throw new Error("Road vehicle save mount is invalid");
    const mount = value as Record<string, unknown>;
    const spec = allSpecs.find((entry) => entry.id === mount.vehicleId);
    if (spec === undefined || typeof mount.seatId !== "string" || !spec.seats.some((seat) => seat.id === mount.seatId)) throw new Error(`Road vehicle save mount for ${playerId} is invalid`);
    const vehicle = normalizedVehicles[spec.id] as unknown as Record<string, unknown>;
    const occupants = vehicle.occupantBySeatId as Record<string, unknown>;
    if (occupants[mount.seatId] !== playerId) throw new Error(`Road vehicle save mount for ${playerId} is inconsistent`);
    const transition = mount.transition;
    if (transition === undefined) {
      normalizedMounts[playerId] = {
        vehicleId: spec.id,
        seatId: mount.seatId,
      };
      continue;
    }
    if (
      typeof transition !== "object" ||
      transition === null ||
      Array.isArray(transition)
    ) {
      throw new Error(`Road vehicle save mount transition for ${playerId} is invalid`);
    }
    const transitionRecord = transition as Record<string, unknown>;
    if (
      (transitionRecord.kind !== "entering" && transitionRecord.kind !== "exiting") ||
      !Number.isFinite(transitionRecord.secondsRemaining) ||
      Number(transitionRecord.secondsRemaining) < 0 ||
      !validPosition(transitionRecord.startPosition) ||
      !validPosition(transitionRecord.endPosition)
    ) {
      throw new Error(`Road vehicle save mount transition for ${playerId} is invalid`);
    }
    normalizedMounts[playerId] = {
      vehicleId: spec.id,
      seatId: mount.seatId,
      transition: {
        kind: transitionRecord.kind,
        secondsRemaining: Number(transitionRecord.secondsRemaining),
        startPosition: { ...transitionRecord.startPosition },
        endPosition: { ...transitionRecord.endPosition },
      },
    };
  }
  return {
    schemaVersion: ROAD_VEHICLE_FLEET_SCHEMA_VERSION,
    tick: Number(record.tick),
    vehiclesById: normalizedVehicles,
    mountByPlayerId: normalizedMounts,
    dynamicSpecsById: Object.fromEntries(
      dynamicSpecs.map((spec) => [spec.id, spec]),
    ),
  };
}

const MAPPED_ROAD_VEHICLE_FAMILIES = new Set<RuntimeParkedVehicleFamily>([
  "compact-hatch",
  "estate-wagon",
  "ordinary-sedan",
  "small-crossover",
  "compact-van",
  "older-boxy-car",
]);

function parseDynamicSpecs(
  value: unknown,
  schemaVersion: number,
): readonly RoadVehicleSpec[] {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Road vehicle save dynamic specs are invalid");
  }
  const specs: RoadVehicleSpec[] = [];
  for (const [id, raw] of Object.entries(value)) {
    if (
      !id.startsWith("mapped-parked:") ||
      typeof raw !== "object" ||
      raw === null ||
      Array.isArray(raw)
    ) {
      throw new Error(`Road vehicle dynamic spec ${id} is invalid`);
    }
    const spec = raw as Record<string, unknown>;
    const tileId = spec.tileId;
    const position = spec.initialPosition;
    const yawRadians = spec.initialYawRadians;
    const colorHex = spec.proceduralBodyColorHex;
    const family = spec.proceduralFamily;
    if (
      typeof tileId !== "string" ||
      !/^-?\d+:-?\d+$/u.test(tileId) ||
      !validPosition(position) ||
      !Number.isFinite(yawRadians) ||
      !Number.isSafeInteger(colorHex) ||
      Number(colorHex) < 0 ||
      Number(colorHex) > 0xffffff
    ) {
      throw new Error(`Road vehicle dynamic spec ${id} is invalid`);
    }
    if (
      family !== undefined &&
      !MAPPED_ROAD_VEHICLE_FAMILIES.has(
        family as RuntimeParkedVehicleFamily,
      )
    ) {
      throw new Error(`Road vehicle dynamic spec ${id} family is invalid`);
    }
    if (schemaVersion >= 4 && family === undefined) {
      throw new Error(`Road vehicle dynamic spec ${id} family is missing`);
    }
    specs.push(mappedParkedRoadVehicle(
      id,
      tileId as `${number}:${number}`,
      position,
      Number(yawRadians),
      Number(colorHex),
      (family as RuntimeParkedVehicleFamily | undefined) ?? "ordinary-sedan",
    ));
  }
  return specs;
}

function finiteOr(value: unknown, fallback: number): number {
  return Number.isFinite(value) ? Number(value) : fallback;
}

function booleanOr(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function validPosition(
  value: unknown,
): value is Readonly<{ x: number; y: number; z: number }> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const position = value as Record<string, unknown>;
  return [position.x, position.y, position.z].every(Number.isFinite);
}
